import {Paper, Box, Chip, alpha, Card, Typography} from '@mui/material'
import {styled} from '@mui/system'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'

// ─── Color Tokens ───────────────────────────────────────────────────────────
const palette = {
  green: '#10b981',
  amber: '#f59e0b',
  red: '#ef4444',
  darkRed: '#991b1b',
  grey50: '#fafafa',
  grey100: '#f5f5f5',
  grey200: '#eeeeee',
  grey300: '#e0e0e0',
  grey600: '#757575',
  grey800: '#424242',
}

export const StyledPaper = styled(Paper)(({theme}) => ({
  borderRadius: 16,
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0,
  overflow: 'hidden',
  boxShadow: 'none',
}))

export const HeaderContainer = styled(Box)(({theme}) => ({
  background: `linear-gradient(135deg, #1e3a5f 0%, #0f2744 100%)`,
  padding: theme.spacing(3, 4),
  color: 'white',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 3,
    background: `linear-gradient(90deg, ${palette.green}, ${palette.amber}, ${palette.red})`,
  },
}))

export const MetricChip = styled(Chip)(({theme}) => ({
  backgroundColor: 'rgba(255,255,255,0.12)',
  color: 'white',
  fontWeight: 500,
  fontSize: '0.72rem',
  '& .MuiChip-icon': {color: 'white'},
  margin: theme.spacing(0.5),
}))

// ─── Performance Colors ─────────────────────────────────────────────────────
export const getPerformanceColor = (value, columnName) => {
  if (columnName === 't') {
    if (value >= 95) return palette.green
    if (value >= 85) return palette.amber
    return palette.red
  }
  if (columnName === 'tplusone') {
    if (value >= 97) return palette.green
    if (value >= 90) return palette.amber
    return palette.red
  }
  if (columnName === 'tplustwo') {
    if (value >= 98) return palette.green
    if (value >= 92) return palette.amber
    return palette.red
  }
  if (columnName === 'tatplusthree') {
    if (value >= 99) return palette.green
    if (value >= 95) return palette.amber
    return palette.red
  }
  if (columnName === 'prepaidDeliveryPercentage') {
    if (value >= 98) return palette.green
    if (value >= 90) return palette.amber
    return palette.red
  }
  if (columnName === 'codDeliveryPercentage') {
    if (value >= 80) return palette.green
    if (value >= 70) return palette.amber
    return palette.red
  }
  if (value >= 85) return palette.green
  if (value >= 75) return palette.amber
  return palette.red
}

// ─── Performance Cell ───────────────────────────────────────────────────────
export const PerformanceCellContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'performanceColor',
})(({performanceColor}) => ({
  display: 'inline-flex',
  flexDirection: 'column',
  gap: 3,
  padding: '6px 10px',
  borderRadius: 6,
  backgroundColor: alpha(performanceColor, 0.06),
  border: `1px solid ${alpha(performanceColor, 0.15)}`,
  minWidth: 60,
  transition: 'all 0.15s ease',
  '&:hover': {
    backgroundColor: alpha(performanceColor, 0.1),
  },
}))

export const PerformanceValue = styled('span', {
  shouldForwardProp: (prop) => prop !== 'performanceColor',
})(({performanceColor}) => ({
  color: performanceColor,
  fontWeight: 700,
  fontSize: '0.95rem',
  lineHeight: 1,
}))

export const PerformanceCount = styled('span')(({theme}) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.68rem',
}))

// ─── Rank ───────────────────────────────────────────────────────────────────
export const RankBadge = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'isTopPerformer',
})(({isTopPerformer}) => ({
  fontWeight: 700,
  backgroundColor: isTopPerformer ? '#fbbf24' : '#e5e7eb',
  color: isTopPerformer ? '#78350f' : '#374151',
  minWidth: 40,
  height: 24,
  fontSize: '0.72rem',
}))

export const RankCircle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'rank',
})(({rank}) => {
  const colors = {
    1: {bg: '#fef3c7', color: '#92400e', border: '#fbbf24'},
    2: {bg: '#f1f5f9', color: '#334155', border: '#94a3b8'},
    3: {bg: '#fef3c7', color: '#92400e', border: '#d97706'},
  }
  const c = colors[rank] || {bg: palette.grey100, color: palette.grey600, border: palette.grey300}
  return {
    width: 26,
    height: 26,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '0.7rem',
    backgroundColor: c.bg,
    color: c.color,
    border: `1.5px solid ${c.border}`,
    flexShrink: 0,
  }
})

// ─── Legend ──────────────────────────────────────────────────────────────────
export const LegendDot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'dotColor',
})(({dotColor}) => ({
  width: 10,
  height: 10,
  borderRadius: 3,
  backgroundColor: dotColor,
}))

// ─── Table Styles ───────────────────────────────────────────────────────────
export const StyledTableContainer = styled(Box)(({theme}) => ({
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: '#f8fafc',
    color: palette.grey800,
    fontWeight: 700,
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    borderBottom: `2px solid ${palette.grey200}`,
    padding: theme.spacing(1.25, 2),
    whiteSpace: 'nowrap',
  },
  '& .MuiTableBody-root .MuiTableRow-root': {
    transition: 'background-color 0.1s ease',
    '&:hover': {
      backgroundColor: '#f5f9ff',
    },
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    padding: theme.spacing(1.25, 2),
    borderBottom: `1px solid ${palette.grey200}`,
    fontSize: '0.8rem',
  },
}))

export const CourierNameCell = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.25),
  fontWeight: 600,
  fontSize: '0.82rem',
  color: palette.grey800,
}))

export const CourierIcon = styled(LocalShippingIcon)({
  color: '#1565c0',
  fontSize: '1rem',
})

// ─── Summary Cards ──────────────────────────────────────────────────────────
export const SummaryCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'borderColor',
})(({theme, borderColor}) => ({
  padding: theme.spacing(2, 2.5),
  borderLeft: `4px solid ${borderColor}`,
  borderRadius: 12,
  boxShadow: 'none',
  border: `1px solid ${palette.grey200}`,
  borderLeftColor: borderColor,
  borderLeftWidth: 4,
  flex: 1,
  minWidth: 0,
  transition: 'all 0.15s ease',
  '&:hover': {
    borderLeftWidth: 4,
    backgroundColor: '#fafcff',
  },
}))

export const SummaryCardValue = styled('span')({
  fontSize: '1.5rem',
  fontWeight: 800,
  lineHeight: 1.2,
  color: palette.grey800,
  display: 'block',
})

export const SummaryCardLabel = styled('span')({
  fontSize: '0.7rem',
  fontWeight: 600,
  color: palette.grey600,
  display: 'block',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  marginBottom: 4,
})

export const SummaryCardSubtext = styled('span')({
  fontSize: '0.68rem',
  color: palette.grey600,
  display: 'block',
  marginTop: 2,
})

// ─── Chart Card ─────────────────────────────────────────────────────────────
export const ChartCard = styled(Paper)(({theme}) => ({
  padding: theme.spacing(2.5),
  borderRadius: 12,
  boxShadow: 'none',
  border: `1px solid ${palette.grey200}`,
}))

// ─── Filter Bar ─────────────────────────────────────────────────────────────
export const FilterBar = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  padding: theme.spacing(1.5, 3),
  backgroundColor: '#ffffff',
  borderTop: `1px solid ${palette.grey200}`,
  borderBottom: `1px solid ${palette.grey200}`,
  flexWrap: 'wrap',
}))
